import { useCallback, useEffect, useState } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  listMembers,
  listInvites,
  createInvite,
  revokeInvite,
  removeMember,
  type ErrorResponse,
  type OrganizationMember,
  type OrganizationMemberInviteResponse,
  type UserOrganization,
} from '@vn-propane/shared';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { UserPlus, Trash2 } from 'lucide-react';

type OutletContext = { organization: UserOrganization };

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
}

export default function MembersPage() {
  const { t } = useTranslation(['admin', 'common']);
  const { id } = useParams<{ id: string }>();
  const { organization } = useOutletContext<OutletContext>();
  const isAdmin = organization.role === 'admin';

  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [invites, setInvites] = useState<OrganizationMemberInviteResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'member'>('member');
  const [isSending, setIsSending] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    setError(null);
    try {
      const membersResult = await listMembers({ path: { org_id: id } });
      if (membersResult.data) {
        setMembers(membersResult.data.data);
      } else if (membersResult.error) {
        setError((membersResult.error as ErrorResponse).error?.message || 'Failed to load members');
      }
      if (isAdmin) {
        const invitesResult = await listInvites({ path: { org_id: id } });
        if (invitesResult.data) {
          setInvites(invitesResult.data.data);
        }
      }
    } catch {
      setError(t('admin:members.unexpectedError', { defaultValue: 'Unexpected error' }));
    } finally {
      setIsLoading(false);
    }
  }, [id, isAdmin, t]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleSendInvite = async () => {
    if (!id) return;
    setInviteError(null);
    const email = inviteEmail.trim();
    if (!email || !email.includes('@')) {
      setInviteError(t('admin:members.emailLabel'));
      return;
    }
    setIsSending(true);
    try {
      const result = await createInvite({
        path: { org_id: id },
        body: { email, role: inviteRole },
      });
      if (result.data) {
        setDialogOpen(false);
        setInviteEmail('');
        setInviteRole('member');
        await refresh();
      } else if (result.error) {
        setInviteError((result.error as ErrorResponse).error?.message || 'Failed to send invite');
      }
    } finally {
      setIsSending(false);
    }
  };

  const handleRevoke = async (inviteId: string) => {
    if (!id) return;
    if (!window.confirm(t('admin:members.confirmRevoke'))) return;
    const result = await revokeInvite({ path: { org_id: id, invite_id: inviteId } });
    if (result.data) {
      await refresh();
    } else if (result.error) {
      setError((result.error as ErrorResponse).error?.message || 'Failed to revoke');
    }
  };

  const handleRemove = async (userId: string) => {
    if (!id) return;
    if (!window.confirm(t('admin:members.confirmRemove'))) return;
    const result = await removeMember({ path: { org_id: id, user_id: userId } });
    if (result.data) {
      await refresh();
    } else if (result.error) {
      const msg = (result.error as ErrorResponse).error?.message || '';
      if (msg.toLowerCase().includes('last admin')) {
        setError(t('admin:members.lastAdmin'));
      } else {
        setError(msg || 'Failed to remove member');
      }
    }
  };

  return (
    <div>
      <div className="flex justify-between items-start mb-6 gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-extrabold tracking-tight">{t('admin:members.title')}</h2>
          <p className="text-muted-foreground">{t('admin:members.description')}</p>
        </div>
        {isAdmin && (
          <Button onClick={() => setDialogOpen(true)}>
            <UserPlus className="h-4 w-4 mr-2" />
            {t('admin:members.inviteButton')}
          </Button>
        )}
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>{t('admin:members.membersSection')}</CardTitle>
          <CardDescription>{organization.name}</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="h-24 bg-muted rounded animate-pulse" />
          ) : members.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('admin:members.noMembers')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="py-2 pr-4 font-medium">{t('admin:members.username')}</th>
                    <th className="py-2 pr-4 font-medium">{t('admin:members.email')}</th>
                    <th className="py-2 pr-4 font-medium">{t('admin:members.role')}</th>
                    <th className="py-2 pr-4 font-medium">{t('admin:members.joinedAt')}</th>
                    {isAdmin && <th className="py-2 pr-4 font-medium text-right">&nbsp;</th>}
                  </tr>
                </thead>
                <tbody>
                  {members.map((m) => (
                    <tr key={m.userId} className="border-b last:border-0">
                      <td className="py-2 pr-4">{m.username}</td>
                      <td className="py-2 pr-4">{m.email}</td>
                      <td className="py-2 pr-4">
                        <Badge variant={m.role === 'admin' ? 'default' : 'secondary'}>
                          {m.role === 'admin' ? t('admin:members.roleAdmin') : t('admin:members.roleMember')}
                        </Badge>
                      </td>
                      <td className="py-2 pr-4">{formatDate(m.joinedAt)}</td>
                      {isAdmin && (
                        <td className="py-2 pr-4 text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemove(m.userId)}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            {t('admin:members.remove')}
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {isAdmin && (
        <Card>
          <CardHeader>
            <CardTitle>{t('admin:members.pendingSection')}</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="h-16 bg-muted rounded animate-pulse" />
            ) : invites.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('admin:members.noPending')}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="py-2 pr-4 font-medium">{t('admin:members.email')}</th>
                      <th className="py-2 pr-4 font-medium">{t('admin:members.role')}</th>
                      <th className="py-2 pr-4 font-medium">{t('admin:members.expires')}</th>
                      <th className="py-2 pr-4 font-medium text-right">&nbsp;</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invites.map((inv) => (
                      <tr key={inv.id} className="border-b last:border-0">
                        <td className="py-2 pr-4">{inv.email}</td>
                        <td className="py-2 pr-4">
                          <Badge variant={inv.role === 'admin' ? 'default' : 'secondary'}>
                            {inv.role === 'admin' ? t('admin:members.roleAdmin') : t('admin:members.roleMember')}
                          </Badge>
                        </td>
                        <td className="py-2 pr-4">{formatDate(inv.expiresAt)}</td>
                        <td className="py-2 pr-4 text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRevoke(inv.id)}
                            className="text-destructive hover:text-destructive"
                          >
                            {t('admin:members.revoke')}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('admin:members.inviteDialogTitle')}</DialogTitle>
            <DialogDescription>{t('admin:members.inviteDialogDescription')}</DialogDescription>
          </DialogHeader>
          {inviteError && (
            <Alert variant="destructive">
              <AlertDescription>{inviteError}</AlertDescription>
            </Alert>
          )}
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="invite-email">{t('admin:members.emailLabel')}</Label>
              <Input
                id="invite-email"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                disabled={isSending}
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="invite-role">{t('admin:members.roleLabel')}</Label>
              <Select
                value={inviteRole}
                onValueChange={(v) => setInviteRole(v as 'admin' | 'member')}
                disabled={isSending}
              >
                <SelectTrigger id="invite-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">{t('admin:members.roleMember')}</SelectItem>
                  <SelectItem value="admin">{t('admin:members.roleAdmin')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={isSending}>
              {t('admin:members.cancel')}
            </Button>
            <Button onClick={handleSendInvite} disabled={isSending}>
              {isSending ? t('admin:members.sending') : t('admin:members.send')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
